import { Collapse } from '../../../components/Collapse'
import { useCallback, useEffect, useState } from 'react'
import { HospitalsTableProps } from '../types'
import { Collapse as CollapseAntd } from 'antd'
import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { ReactComponent as Delete16 } from '../../../assets/svg/Delete16.svg'
import { BTN_TXT, PLACEHOLDERS } from '../../../constants'
import { useCRUDUserPopupProviderContext } from '../../../pages/CRUDUser/Provider/CRUDUserPopupProvider'
import { AddCCPopup } from '../../UserContractCategories/AddCCPopup/AddCCPopup'
import { DeleteContractCategoriesPopup } from '../../UserContractCategories/DeleteContractCategoriesPopup/DeleteContractCategoriesPopup'
import { setLoading } from '../../../redux/store/common/slice'
import { removeCCFromHospitalUserProfile } from '../../UserContractCategories/api'
import { useDispatch } from 'react-redux'
import Table from '../../../components/Table/Table'
import { useHospitalsCCTable } from './useHospitalsCCTable'
import { notification } from '../../../components/Notification'
import { Input } from '../../../components/Input'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'

const { Panel } = CollapseAntd

export const HospitalsTableContainer = ({
  hospitals,
  userId,
  canManageContractCategories,
  updateCallback,
  simpleViewModeOnly,
  canViewVotingPanelMembership,
  canUpdateVotingPanelMembership
}: HospitalsTableProps) => {
  const [activeKey, setActiveKey] = useState('')
  const onChange = useCallback((uuid) => setActiveKey(uuid), [])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [singleSelectedCC, setSingleSelectedCC] = useState<string | null>(null)

  const {
    tableData,
    tableColumns,
    totalNumber,
    pageInfo,
    handleTableChange,
    handleSelectRow,
    handleChangePageSize,
    onUpdateCCList,
    setSearchValue,
    searchValue
  } = useHospitalsCCTable({
    userId,
    activeKey,
    setSelectedRowKeys,
    setSingleSelectedCC,
    canSelectCC: canManageContractCategories,
    updateCallback,
    canViewVotingPanelMembership,
    canUpdateVotingPanelMembership
  })

  const dispatch = useDispatch()

  const onOpenAddModal = () => setIsAddModalOpen(true)

  const { deleteCCPopup } = useCRUDUserPopupProviderContext()

  const onCancelDeleteCallback = useCallback(() => {
    setSingleSelectedCC(null)
  }, [])

  const onDeleteCC = useCallback(async () => {
    if (!selectedRowKeys && !singleSelectedCC) return
    try {
      dispatch(setLoading(true))
      await removeCCFromHospitalUserProfile({
        userId,
        hospitalId: activeKey,
        data: {
          contract_categories: singleSelectedCC
            ? [singleSelectedCC]
            : selectedRowKeys
        }
      })
      notification.success({
        message: `Contract ${
          singleSelectedCC ? 'category has' : 'categories have'
        } been successfully removed.`
      })
      await onUpdateCCList()
      deleteCCPopup.actions.close()
      setSelectedRowKeys([])
      setSingleSelectedCC(null)
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setLoading(false))
    }
  }, [
    deleteCCPopup,
    dispatch,
    selectedRowKeys,
    onUpdateCCList,
    userId,
    singleSelectedCC,
    activeKey
  ])

  useEffect(() => {
    setSingleSelectedCC(null)
    setSelectedRowKeys([])
  }, [activeKey])

  useEffect(() => {
    if (hospitals?.[0] && !activeKey) {
      setActiveKey(hospitals[0].uuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitals])

  return (
    <>
      <Collapse activeKey={activeKey} onChange={onChange}>
        {hospitals?.map((hospital) => (
          <Panel
            key={hospital.uuid}
            header={
              <Typography.Headline6 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                {hospital.name}
              </Typography.Headline6>
            }
          >
            <div className="column gap-4 mt-16 mb-16 mr-24 ml-24">
              <div className="user-profile-contract-categories row space-between full-width mb-4">
                <Input
                  className="allow-clear"
                  propsInput={{
                    allowClear: { clearIcon: <Close16 /> },
                    placeholder: PLACEHOLDERS.SEARCH,
                    prefix: <Search16 />,
                    value: searchValue,
                    onChange: (e) => setSearchValue(e.target.value)
                  }}
                />
                {canManageContractCategories && !simpleViewModeOnly && (
                  <div className="row justify-end">
                    {selectedRowKeys.length ? (
                      <Button
                        type={BUTTON_TYPES.PRIMARY}
                        icon={<Delete16 />}
                        onClick={deleteCCPopup.actions.open}
                        upperCase
                        danger
                      >
                        {BTN_TXT.DELETE}
                      </Button>
                    ) : (
                      <Button
                        onClick={onOpenAddModal}
                        type={BUTTON_TYPES.PRIMARY}
                        upperCase
                      >
                        {BTN_TXT.ASSIGN_CONTRACT_CATEGORY}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {activeKey === hospital.uuid ? (
                <Table
                  dataSource={tableData}
                  className="user-contract-categories-table hospitals-table"
                  columns={tableColumns}
                  onChange={handleTableChange}
                  onChangePage={handleChangePageSize}
                  pageSize={pageInfo.pageSize}
                  pagination={{
                    pageSize: pageInfo.pageSize,
                    current: pageInfo.pageNumber,
                    total: totalNumber
                  }}
                  rowSelection={
                    canManageContractCategories && !simpleViewModeOnly
                      ? {
                          selectedRowKeys,
                          onChange: handleSelectRow
                        }
                      : undefined
                  }
                />
              ) : null}
            </div>
          </Panel>
        ))}
      </Collapse>
      <AddCCPopup
        isOpen={isAddModalOpen}
        userId={userId}
        hospitalId={activeKey}
        setIsOpen={setIsAddModalOpen}
        onUpdateCCList={onUpdateCCList}
      />
      <DeleteContractCategoriesPopup
        onDelete={onDeleteCC}
        isMultiple={!singleSelectedCC && selectedRowKeys.length > 1}
        onCancelCallback={onCancelDeleteCallback}
      />
    </>
  )
}
